import { pascalize } from "humps";
import {
  emptyTextCell,
  nonEditable,
  textCell,
  getHeaderRow,
  chevronCell,
} from "../cells";
import { HEADING_ROW_HEIGHT } from "../constants";
import { formatCurrency } from "@/utils/helpers";

export const CASHINCHANGE_ROW_ID = "cashInChange";

const transformAccountsToRows = (accounts, parentId = null, rowId = 0) => {
  let rId = rowId;
  let rows = [];

  accounts.forEach((account) => {
    const currentRowId = rId;
    rId += 1;
    const hasChildren = account.children && account.children.length > 0;
    const balanceText = account.balance;

    const row = {
      rowId: currentRowId,
      height: HEADING_ROW_HEIGHT,
      cells: [
        chevronCell({
          text: account.groupHeader || account.name,
          parentId,
          isExpanded: true,
          hasChildren,
        }),
        nonEditable(
          textCell({ text: `${formatCurrency(balanceText)}` }),
          "font-bold trial-balance-row justify-content-start"
        ), // Add the balance text
      ],
    };

    rows.push(row);

    if (hasChildren) {
      const { rows: childRows, rowId: newRowId } = transformAccountsToRows(
        account.children,
        currentRowId,
        rId
      );
      rows = rows.concat(childRows);
      rId = newRowId;
    }
  });

  return { rows, rowId: rId };
};

function getAccountsRows(title, rowsData, parentId = null) {
  const result = [];
  let rowNumber = 0;

  const titleRow = [
    {
      // eslint-disable-next-line no-plusplus
      rowId: `${title}${++rowNumber}`,
      height: HEADING_ROW_HEIGHT,
      cells: [
        chevronCell({
          text: title,
          parentId: null,
          isExpanded: true,
          hasChildren: true,
        }),
        nonEditable(
          textCell({ text: `${formatCurrency(rowsData?.balance || 0)}` }),
          "font-bold trial-balance-row justify-content-start"
        ),
      ],
    },
  ];

  if (rowsData) {
    const { rows: accountsRows, rowId } = transformAccountsToRows(
      rowsData?.accounts,
      `${title}${1}`,
      // eslint-disable-next-line no-plusplus
      `${title}${++rowNumber}`
    );
    result.push(...titleRow, ...accountsRows,);
    rowNumber = rowId;
  }
  return result;
}

function formatString(inputString) {
  return inputString.replace(/([A-Z])/g, " $1").trim();
}

function getTopParent(title) {
  let rowNumber = 0;
  if (title) {
    const tempArr = {
      // eslint-disable-next-line no-plusplus
      rowId: `${title}${++rowNumber}`,
      height: HEADING_ROW_HEIGHT,
      cells: [
        chevronCell({
          text: formatString(pascalize(title)),
          parentId: null,
          isExpanded: true,
          hasChildren: true,
        }),
        nonEditable(
          textCell({ text: "" }),
          "font-bold trial-balance-row justify-content-start"
        ),
      ],
    };

    return tempArr;
  }

  return {};
}

function getTotalRow(title, amount, parentId = null) {
  let rowNumber = 0;
  if (title) {
    const tempArr = {
      // eslint-disable-next-line no-plusplus
      rowId: `${title}${++rowNumber}`,
      height: HEADING_ROW_HEIGHT,
      cells: [
        chevronCell({
          text: formatString(pascalize(title)),
          parentId,
          hasChildren: false,
          className: "font-bold trial-balance-row justify-content-start",
        }),
        nonEditable(
          textCell({ text: formatCurrency(amount || 0) }),
          "font-bold trial-balance-row justify-content-start"
        ),
      ],
    };

    return tempArr;
  }

  return {};
}

export function getProfitAndLossRows({ data, columns }) {
  if (data.data.length === 0) {
    return [];
  }
  const { ordinaryIncome, otherIncome, netIncome, operatingIncome } = data.data;

  // income row
  const incomeRow = getAccountsRows(
    "Income",
    ordinaryIncome?.income,
    null
  );

  // cost of sales row
  const costOfSalesRow = getAccountsRows(
    "Cost of Sales",
    ordinaryIncome?.costOfSales,
    null
  );

  // gross profit row
  const grossProfitRow = getTotalRow(
    "Gross Profit",
    ordinaryIncome?.grossProfit.netActivity,
    null
  );
  
  const otherIncomeRow = getAccountsRows(
    "Other Income",
    operatingIncome?.otherIncome,
    null
  );

  const expensesRow = getAccountsRows(
    "Expenses",
    operatingIncome?.expenses,
    null
  );

  const otherExpenseRow = getAccountsRows(
    "Other Expense",
    operatingIncome?.otherExpense,
    null
  );

  const operatingProfitRow = getTotalRow(
    "Operating Profit",
    operatingIncome?.operatingProfit.netActivity,
    null
  );

  const interestIncomeRow = getAccountsRows(
    " Interest Income",
    otherIncome?.interestIncome,
    null
  );

  const financeCostRow = getAccountsRows(
    " Finance Cost",
    otherIncome?.financeCost,
    null
  );

  const financeProfitRow = getTotalRow(
    "Finance Cost / Income",
    otherIncome?.financeProfit.netActivity,
    null
  );

  const netIncomeRow = getTotalRow(
    "Profit",
    netIncome?.profit?.netActivity
  );

  return [
    getHeaderRow(columns),
    ...incomeRow,
    ...costOfSalesRow,
    grossProfitRow,
    ...otherIncomeRow,
    ...expensesRow,
    ...otherExpenseRow,
    operatingProfitRow,
    ...interestIncomeRow,
    ...financeCostRow,
    financeProfitRow,
    netIncomeRow
  ];
}
